'use client';
import React from 'react';
import { useModel } from '@/context/ModelContext';
import { fmtCrShort } from '@/lib/financial-model';
import InputRow from './InputRow';

export default function CapexSection() {
  const { assumptions, updateAssumption, model } = useModel();

  return (
    <div>
      <InputRow
        label="Machinery (5 TPH Complete Line)"
        unit="₹"
        note="Lean good-quality Indian OEM — ~2.5× the 2 TPH machinery cost; includes pellet mill, dryer, conveyor, grinder"
        confidence="Medium"
        value={assumptions?.machineryCapex}
        onChange={(v) => updateAssumption('machineryCapex', v)}
        min={10000000}
        max={80000000}
        step={500000}
      />
      <InputRow
        label="Civil / Shed / Foundations"
        unit="₹"
        note="Larger shed & heavier foundations for 5 TPH machinery — ~1.9× 2 TPH civil"
        confidence="Medium"
        value={assumptions?.civilCapex}
        onChange={(v) => updateAssumption('civilCapex', v)}
        min={2000000}
        max={30000000}
        step={500000}
      />
      <InputRow
        label="Electrical & Installation"
        unit="₹"
        note="Higher connected load at 5 TPH — transformer, panels, cabling, earthing"
        confidence="Medium"
        value={assumptions?.electricalCapex}
        onChange={(v) => updateAssumption('electricalCapex', v)}
        min={1000000}
        max={15000000}
        step={250000}
      />
      <InputRow
        label="Contingency & Preliminary"
        unit="₹"
        note="~8% of hard costs — pre-operative expenses, DPR, legal, registration"
        confidence="Medium"
        value={assumptions?.contingencyCapex}
        onChange={(v) => updateAssumption('contingencyCapex', v)}
        min={500000}
        max={10000000}
        step={250000}
      />
      <InputRow
        label="Initial Working Capital (Feedstock Buffer)"
        unit="₹"
        note="Critical buffer — ~2.2 months RM cost; ~3.5× the 2 TPH WC requirement"
        confidence="High"
        value={assumptions?.workingCapital}
        onChange={(v) => updateAssumption('workingCapital', v)}
        min={2000000}
        max={40000000}
        step={500000}
      />
      <InputRow
        label="Total Project Cost"
        unit="₹"
        note="Calculated: Machinery + Civil + Electrical + Contingency + Working Capital"
        isCalculated
        calculatedValue={fmtCrShort(model?.totalProjectCost)}
        value={model?.totalProjectCost}
        onChange={() => {}}
      />
      <InputRow
        label="Depreciation Base"
        unit="₹"
        note="Calculated: Machinery + Civil + Electrical (excludes Contingency & WC)"
        isCalculated
        calculatedValue={fmtCrShort(model?.depreciationBase)}
        value={model?.depreciationBase}
        onChange={() => {}}
      />
      {/* CAPEX breakdown table */}
      <div className="mt-4">
        <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-2">
          Uses of Funds Breakdown
        </p>
        <div className="overflow-x-auto scrollbar-thin">
          <table className="w-full text-xs">
            <thead>
              <tr className="section-header-row">
                <th className="text-left py-2 px-3 rounded-tl-lg">Item</th>
                <th className="text-right py-2 px-3">Amount (₹)</th>
                <th className="text-right py-2 px-3 rounded-tr-lg">% of Total</th>
              </tr>
            </thead>
            <tbody>
              {[
                { label: 'Machinery (5 TPH line)', value: assumptions?.machineryCapex },
                { label: 'Civil / Shed / Foundations', value: assumptions?.civilCapex },
                { label: 'Electrical & Installation', value: assumptions?.electricalCapex },
                { label: 'Contingency & Preliminary', value: assumptions?.contingencyCapex },
                { label: 'Initial Working Capital', value: assumptions?.workingCapital },
              ]?.map((item, idx) => (
                <tr
                  key={`capex-row-${idx}`}
                  className="border-b border-border/50 hover:bg-muted/30 transition-colors"
                >
                  <td className="py-2 px-3 text-foreground">{item?.label}</td>
                  <td className="py-2 px-3 text-right font-mono-nums text-foreground">
                    {fmtCrShort(item?.value)}
                  </td>
                  <td className="py-2 px-3 text-right font-mono-nums text-muted-foreground">
                    {model?.totalProjectCost > 0
                      ? ((item?.value / model?.totalProjectCost) * 100)?.toFixed(1)
                      : '0.0'}
                    %
                  </td>
                </tr>
              ))}
              <tr className="highlight-row">
                <td className="py-2 px-3 text-foreground rounded-bl-lg">TOTAL PROJECT COST</td>
                <td className="py-2 px-3 text-right font-mono-nums text-primary">
                  {fmtCrShort(model?.totalProjectCost)}
                </td>
                <td className="py-2 px-3 text-right font-mono-nums text-primary rounded-br-lg">
                  100.0%
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
